/* eslint-disable */
import Long from "long";
import _m0 from "protobufjs/minimal";
import { Vector3dPb } from "../../gen/eigen_lcm/Vector3d";

export const protobufPackage = "simulator";

/** A periodic disturbance oscillating about zero. */
export interface PeriodicDisturbance {
  /** N if force disturbance, Nm if torque disturbance, m/s if wind disturbance */
  amplitude:
    | Vector3dPb
    | undefined;
  /** [s] */
  period: number;
  /** [rad] */
  phaseOffset: number;
}

export interface RandomWalkComponent {
  magnitude: number;
  timeConstant: number;
}

export interface RandomWalkBound {
  /**
   * When the value of the random walk is between `start` and `end`, the value will be biased
   * towards `start`, otherwise it will be unaffected by the bound.
   */
  start: number;
  end: number;
}

export interface RandomWalk {
  components: RandomWalkComponent[];
  bounds: RandomWalkBound[];
  mean: number;
  noiseDt: number;
}

export interface RandomWalkWind {
  xyMagnitude: RandomWalk | undefined;
  xyAngle: RandomWalk | undefined;
  z: RandomWalk | undefined;
}

export interface VonKarmanWind {
  /** [m/s] */
  amplitude:
    | Vector3dPb
    | undefined;
  /** [m/s] */
  freeStreamAirSpeed: number;
  /** [m] */
  heightAboveGround: number;
}

/**
 * Disturbances to be applied to the vehicle. This message can be used to generate force [N],
 * torque [Nm], or wind disturbances [m/s]. Disturbances are additive.
 */
export interface DisturbanceRequest {
  utime: number;
  type: DisturbanceRequest_DisturbanceType;
  /**
   * Constant disturbance. This can be combined with the periodic disturbances below to change the
   * mean of the wave.
   * N if force disturbance, Nm if torque disturbance, m/s if wind disturbance
   */
  constant:
    | Vector3dPb
    | undefined;
  /**
   * Square wave profile:
   * t = utime_to_seconds(utime)
   * |
   * |   -------     -------                     <|
   * |   |     |     |     |                      | amplitude
   * |---|-----|-----|-----|--------> time       <|
   * |   |     |     |     |
   * |----     -------     -------
   * |
   * <----------> period [s]
   * <---> phase_offset / (2*pi) * period [s]
   *
   * If you want to get a square wave which goes from 0 to x, you would create a square wave with
   * amplitude x/2 and then add a constant disturbance of x/2.
   */
  squareWaves: PeriodicDisturbance[];
  /**
   * Sine wave profile:
   * t = utime_to_seconds(utime)
   * |
   * |     -        -       -                <|
   * |    / \      / \     /                  | amplitude
   * |---|---|----|---|---|-----> time [s]   <|
   * |\ /     \  /     \ /
   * | -       -        -
   * |
   * <----------> period [s]
   * <---> phase_offset / (2*pi) * period [s]
   */
  sineWaves: PeriodicDisturbance[];
  /**
   * Random disturbance generated with a normal distribution with the specified stdev. Can create
   * a mean offset using the constant field.
   * N if force disturbance, Nm if torque disturbance, m/s if wind disturbance
   */
  normalDistributionStdev?:
    | Vector3dPb
    | undefined;
  /**
   * Random walk disturbance (only currently supported for wind disturbances). Useful for spoofing
   * more realistic wind disturbances.
   */
  randomWalkWind?:
    | RandomWalkWind
    | undefined;
  /** Von Karman wind model. This is a more realistic wind model that is used at the wind wall. */
  vonKarmanWind?:
    | VonKarmanWind
    | undefined;
  /**
   * Path to a CSV file defining a 3D wind field at arbitrarily placed sample points.
   * CSV columns: x, y, z, wx, wy, wz (all in sim frame, SI units).
   * When set, wind velocity is interpolated from the field at the vehicle position
   * each sim step. The wind field overrides constant wind velocity updates.
   */
  windFieldCsvPath?: string | undefined;
}

export enum DisturbanceRequest_DisturbanceType {
  UNKNOWN = 0,
  WIND_DISTURBANCE = 1,
  FORCE_DISTURBANCE = 2,
  TORQUE_DISTURBANCE = 3,
  CLEAR_DISTURBANCES = 4,
  UNRECOGNIZED = -1,
}

export function disturbanceRequest_DisturbanceTypeFromJSON(object: any): DisturbanceRequest_DisturbanceType {
  switch (object) {
    case 0:
    case "UNKNOWN":
      return DisturbanceRequest_DisturbanceType.UNKNOWN;
    case 1:
    case "WIND_DISTURBANCE":
      return DisturbanceRequest_DisturbanceType.WIND_DISTURBANCE;
    case 2:
    case "FORCE_DISTURBANCE":
      return DisturbanceRequest_DisturbanceType.FORCE_DISTURBANCE;
    case 3:
    case "TORQUE_DISTURBANCE":
      return DisturbanceRequest_DisturbanceType.TORQUE_DISTURBANCE;
    case 4:
    case "CLEAR_DISTURBANCES":
      return DisturbanceRequest_DisturbanceType.CLEAR_DISTURBANCES;
    case -1:
    case "UNRECOGNIZED":
    default:
      return DisturbanceRequest_DisturbanceType.UNRECOGNIZED;
  }
}

export function disturbanceRequest_DisturbanceTypeToJSON(object: DisturbanceRequest_DisturbanceType): string {
  switch (object) {
    case DisturbanceRequest_DisturbanceType.UNKNOWN:
      return "UNKNOWN";
    case DisturbanceRequest_DisturbanceType.WIND_DISTURBANCE:
      return "WIND_DISTURBANCE";
    case DisturbanceRequest_DisturbanceType.FORCE_DISTURBANCE:
      return "FORCE_DISTURBANCE";
    case DisturbanceRequest_DisturbanceType.TORQUE_DISTURBANCE:
      return "TORQUE_DISTURBANCE";
    case DisturbanceRequest_DisturbanceType.CLEAR_DISTURBANCES:
      return "CLEAR_DISTURBANCES";
    case DisturbanceRequest_DisturbanceType.UNRECOGNIZED:
    default:
      return "UNRECOGNIZED";
  }
}

function createBasePeriodicDisturbance(): PeriodicDisturbance {
  return { amplitude: undefined, period: 0, phaseOffset: 0 };
}

export const PeriodicDisturbance = {
  encode(message: PeriodicDisturbance, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.amplitude !== undefined) {
      Vector3dPb.encode(message.amplitude, writer.uint32(10).fork()).ldelim();
    }
    if (message.period !== 0) {
      writer.uint32(21).float(message.period);
    }
    if (message.phaseOffset !== 0) {
      writer.uint32(29).float(message.phaseOffset);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): PeriodicDisturbance {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBasePeriodicDisturbance();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.amplitude = Vector3dPb.decode(reader, reader.uint32());
          continue;
        case 2:
          if (tag !== 21) {
            break;
          }

          message.period = reader.float();
          continue;
        case 3:
          if (tag !== 29) {
            break;
          }

          message.phaseOffset = reader.float();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): PeriodicDisturbance {
    return {
      amplitude: isSet(object.amplitude) ? Vector3dPb.fromJSON(object.amplitude) : undefined,
      period: isSet(object.period) ? globalThis.Number(object.period) : 0,
      phaseOffset: isSet(object.phaseOffset) ? globalThis.Number(object.phaseOffset) : 0,
    };
  },

  toJSON(message: PeriodicDisturbance): unknown {
    const obj: any = {};
    if (message.amplitude !== undefined) {
      obj.amplitude = Vector3dPb.toJSON(message.amplitude);
    }
    if (message.period !== 0) {
      obj.period = message.period;
    }
    if (message.phaseOffset !== 0) {
      obj.phaseOffset = message.phaseOffset;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<PeriodicDisturbance>, I>>(base?: I): PeriodicDisturbance {
    return PeriodicDisturbance.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<PeriodicDisturbance>, I>>(object: I): PeriodicDisturbance {
    const message = createBasePeriodicDisturbance();
    message.amplitude = (object.amplitude !== undefined && object.amplitude !== null)
      ? Vector3dPb.fromPartial(object.amplitude)
      : undefined;
    message.period = object.period ?? 0;
    message.phaseOffset = object.phaseOffset ?? 0;
    return message;
  },
};

function createBaseRandomWalkComponent(): RandomWalkComponent {
  return { magnitude: 0, timeConstant: 0 };
}

export const RandomWalkComponent = {
  encode(message: RandomWalkComponent, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.magnitude !== 0) {
      writer.uint32(13).float(message.magnitude);
    }
    if (message.timeConstant !== 0) {
      writer.uint32(21).float(message.timeConstant);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): RandomWalkComponent {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseRandomWalkComponent();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 13) {
            break;
          }

          message.magnitude = reader.float();
          continue;
        case 2:
          if (tag !== 21) {
            break;
          }

          message.timeConstant = reader.float();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): RandomWalkComponent {
    return {
      magnitude: isSet(object.magnitude) ? globalThis.Number(object.magnitude) : 0,
      timeConstant: isSet(object.timeConstant) ? globalThis.Number(object.timeConstant) : 0,
    };
  },

  toJSON(message: RandomWalkComponent): unknown {
    const obj: any = {};
    if (message.magnitude !== 0) {
      obj.magnitude = message.magnitude;
    }
    if (message.timeConstant !== 0) {
      obj.timeConstant = message.timeConstant;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<RandomWalkComponent>, I>>(base?: I): RandomWalkComponent {
    return RandomWalkComponent.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<RandomWalkComponent>, I>>(object: I): RandomWalkComponent {
    const message = createBaseRandomWalkComponent();
    message.magnitude = object.magnitude ?? 0;
    message.timeConstant = object.timeConstant ?? 0;
    return message;
  },
};

function createBaseRandomWalkBound(): RandomWalkBound {
  return { start: 0, end: 0 };
}

export const RandomWalkBound = {
  encode(message: RandomWalkBound, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.start !== 0) {
      writer.uint32(13).float(message.start);
    }
    if (message.end !== 0) {
      writer.uint32(21).float(message.end);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): RandomWalkBound {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseRandomWalkBound();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 13) {
            break;
          }

          message.start = reader.float();
          continue;
        case 2:
          if (tag !== 21) {
            break;
          }

          message.end = reader.float();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): RandomWalkBound {
    return {
      start: isSet(object.start) ? globalThis.Number(object.start) : 0,
      end: isSet(object.end) ? globalThis.Number(object.end) : 0,
    };
  },

  toJSON(message: RandomWalkBound): unknown {
    const obj: any = {};
    if (message.start !== 0) {
      obj.start = message.start;
    }
    if (message.end !== 0) {
      obj.end = message.end;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<RandomWalkBound>, I>>(base?: I): RandomWalkBound {
    return RandomWalkBound.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<RandomWalkBound>, I>>(object: I): RandomWalkBound {
    const message = createBaseRandomWalkBound();
    message.start = object.start ?? 0;
    message.end = object.end ?? 0;
    return message;
  },
};

function createBaseRandomWalk(): RandomWalk {
  return { components: [], bounds: [], mean: 0, noiseDt: 0 };
}

export const RandomWalk = {
  encode(message: RandomWalk, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    for (const v of message.components) {
      RandomWalkComponent.encode(v!, writer.uint32(10).fork()).ldelim();
    }
    for (const v of message.bounds) {
      RandomWalkBound.encode(v!, writer.uint32(18).fork()).ldelim();
    }
    if (message.mean !== 0) {
      writer.uint32(29).float(message.mean);
    }
    if (message.noiseDt !== 0) {
      writer.uint32(37).float(message.noiseDt);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): RandomWalk {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseRandomWalk();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.components.push(RandomWalkComponent.decode(reader, reader.uint32()));
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.bounds.push(RandomWalkBound.decode(reader, reader.uint32()));
          continue;
        case 3:
          if (tag !== 29) {
            break;
          }

          message.mean = reader.float();
          continue;
        case 4:
          if (tag !== 37) {
            break;
          }

          message.noiseDt = reader.float();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): RandomWalk {
    return {
      components: globalThis.Array.isArray(object?.components)
        ? object.components.map((e: any) => RandomWalkComponent.fromJSON(e))
        : [],
      bounds: globalThis.Array.isArray(object?.bounds)
        ? object.bounds.map((e: any) => RandomWalkBound.fromJSON(e))
        : [],
      mean: isSet(object.mean) ? globalThis.Number(object.mean) : 0,
      noiseDt: isSet(object.noiseDt) ? globalThis.Number(object.noiseDt) : 0,
    };
  },

  toJSON(message: RandomWalk): unknown {
    const obj: any = {};
    if (message.components?.length) {
      obj.components = message.components.map((e) => RandomWalkComponent.toJSON(e));
    }
    if (message.bounds?.length) {
      obj.bounds = message.bounds.map((e) => RandomWalkBound.toJSON(e));
    }
    if (message.mean !== 0) {
      obj.mean = message.mean;
    }
    if (message.noiseDt !== 0) {
      obj.noiseDt = message.noiseDt;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<RandomWalk>, I>>(base?: I): RandomWalk {
    return RandomWalk.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<RandomWalk>, I>>(object: I): RandomWalk {
    const message = createBaseRandomWalk();
    message.components = object.components?.map((e) => RandomWalkComponent.fromPartial(e)) || [];
    message.bounds = object.bounds?.map((e) => RandomWalkBound.fromPartial(e)) || [];
    message.mean = object.mean ?? 0;
    message.noiseDt = object.noiseDt ?? 0;
    return message;
  },
};

function createBaseRandomWalkWind(): RandomWalkWind {
  return { xyMagnitude: undefined, xyAngle: undefined, z: undefined };
}

export const RandomWalkWind = {
  encode(message: RandomWalkWind, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.xyMagnitude !== undefined) {
      RandomWalk.encode(message.xyMagnitude, writer.uint32(10).fork()).ldelim();
    }
    if (message.xyAngle !== undefined) {
      RandomWalk.encode(message.xyAngle, writer.uint32(18).fork()).ldelim();
    }
    if (message.z !== undefined) {
      RandomWalk.encode(message.z, writer.uint32(26).fork()).ldelim();
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): RandomWalkWind {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseRandomWalkWind();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.xyMagnitude = RandomWalk.decode(reader, reader.uint32());
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.xyAngle = RandomWalk.decode(reader, reader.uint32());
          continue;
        case 3:
          if (tag !== 26) {
            break;
          }

          message.z = RandomWalk.decode(reader, reader.uint32());
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): RandomWalkWind {
    return {
      xyMagnitude: isSet(object.xyMagnitude) ? RandomWalk.fromJSON(object.xyMagnitude) : undefined,
      xyAngle: isSet(object.xyAngle) ? RandomWalk.fromJSON(object.xyAngle) : undefined,
      z: isSet(object.z) ? RandomWalk.fromJSON(object.z) : undefined,
    };
  },

  toJSON(message: RandomWalkWind): unknown {
    const obj: any = {};
    if (message.xyMagnitude !== undefined) {
      obj.xyMagnitude = RandomWalk.toJSON(message.xyMagnitude);
    }
    if (message.xyAngle !== undefined) {
      obj.xyAngle = RandomWalk.toJSON(message.xyAngle);
    }
    if (message.z !== undefined) {
      obj.z = RandomWalk.toJSON(message.z);
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<RandomWalkWind>, I>>(base?: I): RandomWalkWind {
    return RandomWalkWind.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<RandomWalkWind>, I>>(object: I): RandomWalkWind {
    const message = createBaseRandomWalkWind();
    message.xyMagnitude = (object.xyMagnitude !== undefined && object.xyMagnitude !== null)
      ? RandomWalk.fromPartial(object.xyMagnitude)
      : undefined;
    message.xyAngle = (object.xyAngle !== undefined && object.xyAngle !== null)
      ? RandomWalk.fromPartial(object.xyAngle)
      : undefined;
    message.z = (object.z !== undefined && object.z !== null) ? RandomWalk.fromPartial(object.z) : undefined;
    return message;
  },
};

function createBaseVonKarmanWind(): VonKarmanWind {
  return { amplitude: undefined, freeStreamAirSpeed: 0, heightAboveGround: 0 };
}

export const VonKarmanWind = {
  encode(message: VonKarmanWind, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.amplitude !== undefined) {
      Vector3dPb.encode(message.amplitude, writer.uint32(10).fork()).ldelim();
    }
    if (message.freeStreamAirSpeed !== 0) {
      writer.uint32(21).float(message.freeStreamAirSpeed);
    }
    if (message.heightAboveGround !== 0) {
      writer.uint32(29).float(message.heightAboveGround);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): VonKarmanWind {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseVonKarmanWind();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.amplitude = Vector3dPb.decode(reader, reader.uint32());
          continue;
        case 2:
          if (tag !== 21) {
            break;
          }

          message.freeStreamAirSpeed = reader.float();
          continue;
        case 3:
          if (tag !== 29) {
            break;
          }

          message.heightAboveGround = reader.float();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): VonKarmanWind {
    return {
      amplitude: isSet(object.amplitude) ? Vector3dPb.fromJSON(object.amplitude) : undefined,
      freeStreamAirSpeed: isSet(object.freeStreamAirSpeed) ? globalThis.Number(object.freeStreamAirSpeed) : 0,
      heightAboveGround: isSet(object.heightAboveGround) ? globalThis.Number(object.heightAboveGround) : 0,
    };
  },

  toJSON(message: VonKarmanWind): unknown {
    const obj: any = {};
    if (message.amplitude !== undefined) {
      obj.amplitude = Vector3dPb.toJSON(message.amplitude);
    }
    if (message.freeStreamAirSpeed !== 0) {
      obj.freeStreamAirSpeed = message.freeStreamAirSpeed;
    }
    if (message.heightAboveGround !== 0) {
      obj.heightAboveGround = message.heightAboveGround;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<VonKarmanWind>, I>>(base?: I): VonKarmanWind {
    return VonKarmanWind.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<VonKarmanWind>, I>>(object: I): VonKarmanWind {
    const message = createBaseVonKarmanWind();
    message.amplitude = (object.amplitude !== undefined && object.amplitude !== null)
      ? Vector3dPb.fromPartial(object.amplitude)
      : undefined;
    message.freeStreamAirSpeed = object.freeStreamAirSpeed ?? 0;
    message.heightAboveGround = object.heightAboveGround ?? 0;
    return message;
  },
};

function createBaseDisturbanceRequest(): DisturbanceRequest {
  return {
    utime: 0,
    type: 0,
    constant: undefined,
    squareWaves: [],
    sineWaves: [],
    normalDistributionStdev: undefined,
    randomWalkWind: undefined,
    vonKarmanWind: undefined,
    windFieldCsvPath: undefined,
  };
}

export const DisturbanceRequest = {
  encode(message: DisturbanceRequest, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.utime !== 0) {
      writer.uint32(8).int64(message.utime);
    }
    if (message.type !== 0) {
      writer.uint32(16).int32(message.type);
    }
    if (message.constant !== undefined) {
      Vector3dPb.encode(message.constant, writer.uint32(26).fork()).ldelim();
    }
    for (const v of message.squareWaves) {
      PeriodicDisturbance.encode(v!, writer.uint32(34).fork()).ldelim();
    }
    for (const v of message.sineWaves) {
      PeriodicDisturbance.encode(v!, writer.uint32(42).fork()).ldelim();
    }
    if (message.normalDistributionStdev !== undefined) {
      Vector3dPb.encode(message.normalDistributionStdev, writer.uint32(50).fork()).ldelim();
    }
    if (message.randomWalkWind !== undefined) {
      RandomWalkWind.encode(message.randomWalkWind, writer.uint32(58).fork()).ldelim();
    }
    if (message.vonKarmanWind !== undefined) {
      VonKarmanWind.encode(message.vonKarmanWind, writer.uint32(66).fork()).ldelim();
    }
    if (message.windFieldCsvPath !== undefined) {
      writer.uint32(74).string(message.windFieldCsvPath);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): DisturbanceRequest {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseDisturbanceRequest();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 8) {
            break;
          }

          message.utime = longToNumber(reader.int64() as Long);
          continue;
        case 2:
          if (tag !== 16) {
            break;
          }

          message.type = reader.int32() as any;
          continue;
        case 3:
          if (tag !== 26) {
            break;
          }

          message.constant = Vector3dPb.decode(reader, reader.uint32());
          continue;
        case 4:
          if (tag !== 34) {
            break;
          }

          message.squareWaves.push(PeriodicDisturbance.decode(reader, reader.uint32()));
          continue;
        case 5:
          if (tag !== 42) {
            break;
          }

          message.sineWaves.push(PeriodicDisturbance.decode(reader, reader.uint32()));
          continue;
        case 6:
          if (tag !== 50) {
            break;
          }

          message.normalDistributionStdev = Vector3dPb.decode(reader, reader.uint32());
          continue;
        case 7:
          if (tag !== 58) {
            break;
          }

          message.randomWalkWind = RandomWalkWind.decode(reader, reader.uint32());
          continue;
        case 8:
          if (tag !== 66) {
            break;
          }

          message.vonKarmanWind = VonKarmanWind.decode(reader, reader.uint32());
          continue;
        case 9:
          if (tag !== 74) {
            break;
          }

          message.windFieldCsvPath = reader.string();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): DisturbanceRequest {
    return {
      utime: isSet(object.utime) ? globalThis.Number(object.utime) : 0,
      type: isSet(object.type) ? disturbanceRequest_DisturbanceTypeFromJSON(object.type) : 0,
      constant: isSet(object.constant) ? Vector3dPb.fromJSON(object.constant) : undefined,
      squareWaves: globalThis.Array.isArray(object?.squareWaves)
        ? object.squareWaves.map((e: any) => PeriodicDisturbance.fromJSON(e))
        : [],
      sineWaves: globalThis.Array.isArray(object?.sineWaves)
        ? object.sineWaves.map((e: any) => PeriodicDisturbance.fromJSON(e))
        : [],
      normalDistributionStdev: isSet(object.normalDistributionStdev)
        ? Vector3dPb.fromJSON(object.normalDistributionStdev)
        : undefined,
      randomWalkWind: isSet(object.randomWalkWind) ? RandomWalkWind.fromJSON(object.randomWalkWind) : undefined,
      vonKarmanWind: isSet(object.vonKarmanWind) ? VonKarmanWind.fromJSON(object.vonKarmanWind) : undefined,
      windFieldCsvPath: isSet(object.windFieldCsvPath) ? globalThis.String(object.windFieldCsvPath) : undefined,
    };
  },

  toJSON(message: DisturbanceRequest): unknown {
    const obj: any = {};
    if (message.utime !== 0) {
      obj.utime = Math.round(message.utime);
    }
    if (message.type !== 0) {
      obj.type = disturbanceRequest_DisturbanceTypeToJSON(message.type);
    }
    if (message.constant !== undefined) {
      obj.constant = Vector3dPb.toJSON(message.constant);
    }
    if (message.squareWaves?.length) {
      obj.squareWaves = message.squareWaves.map((e) => PeriodicDisturbance.toJSON(e));
    }
    if (message.sineWaves?.length) {
      obj.sineWaves = message.sineWaves.map((e) => PeriodicDisturbance.toJSON(e));
    }
    if (message.normalDistributionStdev !== undefined) {
      obj.normalDistributionStdev = Vector3dPb.toJSON(message.normalDistributionStdev);
    }
    if (message.randomWalkWind !== undefined) {
      obj.randomWalkWind = RandomWalkWind.toJSON(message.randomWalkWind);
    }
    if (message.vonKarmanWind !== undefined) {
      obj.vonKarmanWind = VonKarmanWind.toJSON(message.vonKarmanWind);
    }
    if (message.windFieldCsvPath !== undefined) {
      obj.windFieldCsvPath = message.windFieldCsvPath;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<DisturbanceRequest>, I>>(base?: I): DisturbanceRequest {
    return DisturbanceRequest.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<DisturbanceRequest>, I>>(object: I): DisturbanceRequest {
    const message = createBaseDisturbanceRequest();
    message.utime = object.utime ?? 0;
    message.type = object.type ?? 0;
    message.constant = (object.constant !== undefined && object.constant !== null)
      ? Vector3dPb.fromPartial(object.constant)
      : undefined;
    message.squareWaves = object.squareWaves?.map((e) => PeriodicDisturbance.fromPartial(e)) || [];
    message.sineWaves = object.sineWaves?.map((e) => PeriodicDisturbance.fromPartial(e)) || [];
    message.normalDistributionStdev =
      (object.normalDistributionStdev !== undefined && object.normalDistributionStdev !== null)
        ? Vector3dPb.fromPartial(object.normalDistributionStdev)
        : undefined;
    message.randomWalkWind = (object.randomWalkWind !== undefined && object.randomWalkWind !== null)
      ? RandomWalkWind.fromPartial(object.randomWalkWind)
      : undefined;
    message.vonKarmanWind = (object.vonKarmanWind !== undefined && object.vonKarmanWind !== null)
      ? VonKarmanWind.fromPartial(object.vonKarmanWind)
      : undefined;
    message.windFieldCsvPath = object.windFieldCsvPath ?? undefined;
    return message;
  },
};

type Builtin = Date | Function | Uint8Array | string | number | boolean | undefined;

export type DeepPartial<T> = T extends Builtin ? T
  : T extends globalThis.Array<infer U> ? globalThis.Array<DeepPartial<U>>
  : T extends ReadonlyArray<infer U> ? ReadonlyArray<DeepPartial<U>>
  : T extends {} ? { [K in keyof T]?: DeepPartial<T[K]> }
  : Partial<T>;

type KeysOfUnion<T> = T extends T ? keyof T : never;
export type Exact<P, I extends P> = P extends Builtin ? P
  : P & { [K in keyof P]: Exact<P[K], I[K]> } & { [K in Exclude<keyof I, KeysOfUnion<P>>]: never };

function longToNumber(long: Long): number {
  if (long.gt(globalThis.Number.MAX_SAFE_INTEGER)) {
    throw new globalThis.Error("Value is larger than Number.MAX_SAFE_INTEGER");
  }
  return long.toNumber();
}

if (_m0.util.Long !== Long) {
  _m0.util.Long = Long as any;
  _m0.configure();
}

function isSet(value: any): boolean {
  return value !== null && value !== undefined;
}
